import { useCallback, useMemo, useReducer } from "react"
import {
  emptyPlatformSelection,
  reducePlatformSelection,
  selectionFaceTiles,
  selectionSectors,
  selectionSurfaceGrid,
  selectionSurfaceSelect,
  selectionToolMode,
  type PlatformSelectionState,
  type PlatformSector,
} from "@/lib/location-scene-3d/platform-selection"
import type { FaceTile, SceneCell, ScenePlatform } from "@/lib/location-scene-3d/platform"

export type PlatformSectorsOpts = { prelude?: boolean }

type UsePlatformSelectionArgs = {
  platform: ScenePlatform | null
  cells: ReadonlyArray<SceneCell>
}

export function usePlatformSelection({ platform, cells }: UsePlatformSelectionArgs) {
  const [state, dispatch] = useReducer(reducePlatformSelection, undefined, emptyPlatformSelection)

  const clear = useCallback(() => {
    dispatch({ type: "clear" })
  }, [])

  const handlers = useMemo(
    () => ({
      sectors: (sectors: PlatformSector[], additive: boolean, opts?: PlatformSectorsOpts) => {
        dispatch({ type: "sectors", sectors, additive, prelude: opts?.prelude })
      },
      face: (faceKey: string | null, additive = false) => {
        dispatch({ type: "face", faceKey, additive, cells, platform })
      },
      faceTiles: (tiles: FaceTile[], additive: boolean) => {
        dispatch({ type: "faceTiles", tiles, additive, cells, platform })
      },
      clear: () => {
        dispatch({ type: "clear" })
      },
    }),
    [cells, platform],
  )

  const replaceFaceTiles = useCallback((tiles: FaceTile[]) => {
    dispatch({ type: "replaceFaceTiles", tiles })
  }, [])

  const filterFaceTiles = useCallback((keep: (tile: FaceTile) => boolean) => {
    dispatch({ type: "filterFaceTiles", keep })
  }, [])

  return {
    state,
    selectedSectors: selectionSectors(state),
    selectedFaceTiles: selectionFaceTiles(state),
    surfaceSelect: selectionSurfaceSelect(state),
    surfaceGrid: selectionSurfaceGrid(state),
    mode: selectionToolMode(state),
    panelOpen: state.panelOpen,
    clear,
    handlers,
    replaceFaceTiles,
    filterFaceTiles,
    dispatch,
  }
}

export type { PlatformSelectionState }
